import { toPng, getFontEmbedCSS } from "html-to-image";
import { jsPDF } from "jspdf";

const A4 = { w: 210, h: 297 };
const MARK = 3;
const MARK_GAP = 1;

function cropMarks(x, y, w, h, off = 0) {
  const L = [];
  [[x, y], [x + w, y], [x, y + h], [x + w, y + h]].forEach(([cx, cy], i) => {
    const dx = i % 2 === 0 ? -1 : 1;
    const dy = i < 2 ? -1 : 1;
    L.push({ x1: cx + dx * (MARK_GAP + off), y1: cy, x2: cx + dx * (MARK_GAP + off + MARK), y2: cy });
    L.push({ x1: cx, y1: cy + dy * (MARK_GAP + off), x2: cx, y2: cy + dy * (MARK_GAP + off + MARK) });
  });
  return L;
}

// items: [{ key, data }]; returns pages [{ w, h, cells: [{ key, side, x, y, w, h }], lines }]
function applyCalib(pages, calib) {
  if (!calib) return pages;
  const sc = (calib.scale || 100) / 100;
  return pages.map((p) => {
    const side = p.cells[0]?.side;
    const ox = side === "back" ? calib.back_offset_x || 0 : calib.offset_x || 0;
    const oy = side === "back" ? calib.back_offset_y || 0 : calib.offset_y || 0;
    return {
      ...p,
      cells: p.cells.map((c) => ({ ...c, x: c.x + (c.w - c.w * sc) / 2 + ox, y: c.y + (c.h - c.h * sc) / 2 + oy, w: c.w * sc, h: c.h * sc })),
      lines: p.lines.map((l) => ({ x1: l.x1 + ox, x2: l.x2 + ox, y1: l.y1 + oy, y2: l.y2 + oy })),
    };
  });
}

export function buildPages(tpl, items, opts) {
  return applyCalib(layoutPages(tpl, items, opts), opts.calib);
}

const withBleed = (c, b) => ({ ...c, x: c.x - b, y: c.y - b, w: c.w + 2 * b, h: c.h + 2 * b });

function layoutPages(tpl, items, { layout, sides, marks, bleed = 0 }) {
  const { width: cw, height: ch } = tpl;
  const b = bleed || 0;
  const sideList = sides === "both" ? ["front", "back"] : [sides];
  if (layout === "card") {
    const pw = cw + 2 * b, ph = ch + 2 * b;
    return items.flatMap((it) => sideList.map((side) => ({ w: pw, h: ph, cells: [{ ...it, side, x: 0, y: 0, w: pw, h: ph }], lines: [] })));
  }
  const gap = Math.max(marks ? 10 : 4, 2 * b + 2);
  const margin = 10;
  const cols = Math.max(1, Math.floor((A4.w - 2 * margin + gap) / (cw + gap)));
  const rows = Math.max(1, Math.floor((A4.h - 2 * margin + gap) / (ch + gap)));
  const per = cols * rows;
  const ox = (A4.w - (cols * cw + (cols - 1) * gap)) / 2;
  const oy = (A4.h - (rows * ch + (rows - 1) * gap)) / 2;
  const pages = [];
  for (let i = 0; i < items.length; i += per) {
    const chunk = items.slice(i, i + per);
    sideList.forEach((side) => {
      const cells = chunk.map((it, j) => {
        const r = Math.floor(j / cols);
        const c = side === "back" && sides === "both" ? cols - 1 - (j % cols) : j % cols;
        return { ...it, side, x: ox + c * (cw + gap), y: oy + r * (ch + gap), w: cw, h: ch };
      });
      pages.push({ w: A4.w, h: A4.h, cells: cells.map((c) => withBleed(c, b)), lines: marks ? cells.flatMap((c) => cropMarks(c.x, c.y, c.w, c.h, b)) : [] });
    });
  }
  return pages;
}

export async function renderImages(container, onProgress) {
  const nodes = [...container.querySelectorAll("[data-render-key]")];
  const imgs = [...container.querySelectorAll("img")];
  await Promise.all(imgs.map((im) => (im.complete ? null : new Promise((r) => { im.onload = r; im.onerror = r; }))));
  await document.fonts.ready;
  let fontEmbedCSS;
  try { fontEmbedCSS = await getFontEmbedCSS(container); } catch { fontEmbedCSS = ""; }
  const out = {};
  for (let i = 0; i < nodes.length; i++) {
    out[nodes[i].dataset.renderKey] = await toPng(nodes[i], { pixelRatio: 3, fontEmbedCSS, cacheBust: false });
    onProgress?.(i + 1, nodes.length);
  }
  return out;
}

const imgKey = (c) => `${c.key}-${c.side}`;

export function makePdf(pages, images, filename) {
  const orient = (p) => (p.w > p.h ? "landscape" : "portrait");
  const pdf = new jsPDF({ unit: "mm", format: [pages[0].w, pages[0].h], orientation: orient(pages[0]), compress: true });
  pages.forEach((p, i) => {
    if (i > 0) pdf.addPage([p.w, p.h], orient(p));
    p.cells.forEach((c) => { const src = images[imgKey(c)]; if (src) pdf.addImage(src, "PNG", c.x, c.y, c.w, c.h, undefined, "FAST"); });
    pdf.setLineWidth(0.15);
    pdf.setDrawColor(0);
    p.lines.forEach((l) => pdf.line(l.x1, l.y1, l.x2, l.y2));
  });
  pdf.save(filename);
}

export function browserPrint(pages, images) {
  const p0 = pages[0];
  const html = pages.map((p) => `<div class="page">${p.cells.map((c) => `<img src="${images[imgKey(c)] || ""}" style="left:${c.x}mm;top:${c.y}mm;width:${c.w}mm;height:${c.h}mm"/>`).join("")}${p.lines.map((l) => {
    const horiz = l.y1 === l.y2;
    const x = Math.min(l.x1, l.x2), y = Math.min(l.y1, l.y2);
    return `<i style="left:${x}mm;top:${y}mm;width:${horiz ? Math.abs(l.x2 - l.x1) : 0.15}mm;height:${horiz ? 0.15 : Math.abs(l.y2 - l.y1)}mm"></i>`;
  }).join("")}</div>`).join("");
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(frame);
  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><style>@page{size:${p0.w}mm ${p0.h}mm;margin:0}html,body{margin:0;padding:0}.page{position:relative;width:${p0.w}mm;height:${p0.h}mm;overflow:hidden;page-break-after:always;break-after:page}.page:last-child{page-break-after:auto}img,i{position:absolute;display:block}i{background:#000}</style></head><body>${html}</body></html>`);
  doc.close();
  setTimeout(() => {
    frame.contentWindow.focus();
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 2000);
  }, 600);
}

export { imgKey };
